import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import AppShell, { CartButton } from './AppShell';
import Sheet from './Sheet';
import Reveal from './Reveal';
import {
    API_BASE, formatCurrency, formatDate, formatDateShort,
    getDeliveryDates, TIME_SLOTS,
} from '../config/api';
import { isFullyPaid, isPartial, paidAmountOf, amountDueOf, refundDueOf } from '../billPayment';
import './MyOrdersPage.css';

const TABS = [
    { id: 'all', label: 'Tất cả' },
    { id: 'pending', label: 'Chờ giao' },
    { id: 'done', label: 'Đã giao' },
];

const statusBadge = (status, paymentStatus) => {
    const s = (status || '').toLowerCase();
    const ps = (paymentStatus || '').toLowerCase();
    if (s.includes('hủy') || ps === 'cancelled') return { text: status || 'Đã hủy', cls: 'cancel' };
    if (ps === 'partial') return { text: 'Cần trả thêm chênh lệch', cls: 'wait' };
    if (s.includes('đã giao')) return { text: status, cls: 'done' };
    if (s.includes('đang giao')) return { text: status, cls: 'ship' };
    if (s.includes('làm') || s.includes('chuẩn bị')) return { text: status, cls: 'make' };
    if (s.includes('đã thanh toán') || ps === 'paid') return { text: status || 'Đã thanh toán', cls: 'paid' };
    if (s.includes('chờ') || ps === 'pending') return { text: status || 'Chờ thanh toán', cls: 'wait' };
    return { text: status || 'Chờ thanh toán', cls: 'wait' };
};

const timelineSteps = (status, paymentStatus) => {
    const s = (status || '').toLowerCase();
    const ps = (paymentStatus || '').toLowerCase();

    const isCancelled = s.includes('hủy') || ps === 'cancelled';
    const isWait = s.includes('chờ') && ps !== 'paid';
    const partial = ps === 'partial' && !isCancelled;

    // Same rule as the admin list: a pay-later order being baked is not paid yet.
    const paid = isFullyPaid({ status, paymentStatus });
    const baking = !isWait && !isCancelled && (s.includes('bánh') || s.includes('giao'));
    const shipping = !isWait && !isCancelled && (s.includes('giao hàng') || s.includes('đang giao') || s.includes('đã giao'));
    const done = !isWait && !isCancelled && s.includes('đã giao');

    return [
        { label: 'Nhận đơn', icon: 'fa-solid fa-check', state: 'done' },
        {
            label: partial ? 'Trả thêm chênh lệch' : paid ? 'Đã thanh toán' : 'Chờ thanh toán',
            icon: paid ? 'fa-solid fa-check' : 'fa-solid fa-wallet',
            state: paid ? 'done' : isCancelled ? 'todo' : 'now',
        },
        { label: 'Làm bánh', icon: shipping ? 'fa-solid fa-check' : 'fa-solid fa-fire-burner', state: shipping ? 'done' : baking ? 'now' : 'todo' },
        { label: done ? 'Đã giao' : 'Giao hàng', icon: done ? 'fa-solid fa-check' : 'fa-solid fa-truck', state: done ? 'done' : shipping ? 'now' : 'todo' },
    ];
};

const summarizeItems = (items) => {
    if (!items) return 'Đơn bánh';
    try {
        const arr = typeof items === 'string' ? JSON.parse(items) : items;
        if (!Array.isArray(arr)) return 'Đơn bánh';
        return arr.map((it) => {
            const qty = it.qty || 1;
            const subCakes = Array.isArray(it.items) ? it.items : [];
            // If it's a box with cakes, box slot details are rendered below so keep title concise
            if (it.isBox || subCakes.length > 1) {
                return `${qty}× ${it.name}`;
            }
            if (subCakes.length === 1 && !it.isBox) {
                const s = subCakes[0];
                const eggStr = s.hasEgg !== undefined ? (s.hasEgg ? '1 trứng' : 'không trứng') : (s.egg || '');
                const tag = [s.weight, eggStr].filter(Boolean).join(', ');
                return `${qty}× ${it.name}${tag ? ` (${tag})` : ''}`;
            }
            if (it.itemsSummary) {
                return `${qty}× ${it.name} (${it.itemsSummary})`;
            }
            return `${qty}× ${it.name}`;
        }).join(' · ');
    } catch (e) {
        return String(items);
    }
};

// Lazy import keeps the payment sheet out of the first paint
const SepayModal = React.lazy(() => import('./SepayModal'));

export default function MyOrdersPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const dates = getDeliveryDates();

    const searchStr = location?.search || (typeof window !== 'undefined' ? window.location.search : '');
    const searchParams = useMemo(() => new URLSearchParams(searchStr), [searchStr]);
    const paramQuery = useMemo(() => {
        return (
            searchParams.get('code') ||
            searchParams.get('orderCode') ||
            searchParams.get('phone') ||
            searchParams.get('query') ||
            searchParams.get('q') ||
            ''
        ).trim();
    }, [searchParams]);

    const savedPhone = typeof localStorage !== 'undefined' ? (localStorage.getItem('customerPhone') || '') : '';
    const savedCode = typeof localStorage !== 'undefined' ? (localStorage.getItem('lastOrderCode') || '') : '';

    const [query, setQuery] = useState(paramQuery || savedPhone || savedCode);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [tab, setTab] = useState('all');

    const [payBill, setPayBill] = useState(null);
    const [showPay, setShowPay] = useState(false);

    const [editBill, setEditBill] = useState(null);
    const [newDate, setNewDate] = useState('');
    const [newSlot, setNewSlot] = useState(TIME_SLOTS[0]);

    const fetchOrders = useCallback(async (raw) => {
        const q = (raw || '').trim();
        if (!q) { setOrders([]); setSearched(false); return; }
        setLoading(true);
        setSearched(true);
        try {
            const res = await axios.get(`${API_BASE}/bill/findByPhoneOrCode?query=${encodeURIComponent(q)}`);
            const data = Array.isArray(res.data) ? res.data : [];
            setOrders(data);
            if (data.length > 0 && typeof localStorage !== 'undefined') {
                if (data[0].customerPhone) localStorage.setItem('customerPhone', data[0].customerPhone);
                if (data[0].orderCode) localStorage.setItem('lastOrderCode', data[0].orderCode);
            }
        } catch (err) {
            console.error('Lỗi tra cứu đơn:', err);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const initial = (paramQuery || savedPhone || savedCode).trim();
        if (initial) {
            setQuery(initial);
            fetchOrders(initial);
        }
    }, [paramQuery, savedPhone, savedCode, fetchOrders]);

    const onSearch = (e) => {
        e.preventDefault();
        if (!query.trim()) {
            Swal.fire({
                icon: 'info',
                title: 'Nhập thông tin tra cứu',
                text: 'Vui lòng nhập số điện thoại hoặc mã đơn đã dùng khi đặt bánh.',
                confirmButtonColor: '#142C54',
            });
            return;
        }
        if (document.activeElement) document.activeElement.blur();
        fetchOrders(query);
    };

    const openEdit = (bill) => {
        setEditBill(bill);
        const bDate = bill.delivery_date ? bill.delivery_date.split('T')[0] : '';
        setNewDate(dates.some((d) => d.iso === bDate) ? bDate : dates[0]?.iso);
        setNewSlot(bill.deliveryTimeSlot || TIME_SLOTS[0]);
    };

    const saveDate = async () => {
        if (!editBill) return;
        try {
            await axios.put(`${API_BASE}/bill/editDeliveryDate/${editBill.id}`, {
                delivery_date: newDate,
                deliveryTimeSlot: newSlot,
            });
            setOrders((prev) => prev.map((o) => (
                o.id === editBill.id ? { ...o, delivery_date: newDate, deliveryTimeSlot: newSlot } : o
            )));
            setEditBill(null);
            Swal.fire({
                icon: 'success',
                title: 'Đã đổi ngày giao',
                text: `Đơn #${editBill.orderCode} sẽ giao ${formatDateShort(newDate)}.`,
                confirmButtonColor: '#142C54',
            });
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Không cập nhật được',
                text: 'Không thể đổi ngày giao lúc này. Vui lòng thử lại!',
                confirmButtonColor: '#142C54',
            });
        }
    };

    const filtered = orders.filter((o) => {
        const done = (o.status || '').toLowerCase().includes('đã giao');
        if (tab === 'pending') return !done && !(o.status || '').includes('hủy');
        if (tab === 'done') return done;
        return true;
    });

    return (
        <AppShell
            title="Đơn của tôi"
            subtitle="Tra cứu &amp; theo dõi đơn đã đặt"
            right={<CartButton />}
            crumbs={[{ label: 'Đơn của tôi' }]}
        >
            <div className="shell orders-layout">
                <div className="orders-main">
                {/* Search */}
                <form className="order-search" onSubmit={onSearch}>
                    <div className="search-box">
                        <i className="fa-solid fa-magnifying-glass" />
                        <input
                            type="search"
                            inputMode="text"
                            placeholder="Số điện thoại hoặc mã đơn"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            aria-label="Số điện thoại hoặc mã đơn"
                        />
                    </div>
                    <button type="submit" className="btn btn-primary btn-sm order-search-btn">Tra cứu</button>
                </form>

                {/* Tabs */}
                {orders.length > 0 && (
                    <div className="seg orders-seg">
                        {TABS.map((t) => (
                            <button key={t.id} type="button" className={tab === t.id ? 'on' : ''} onClick={() => setTab(t.id)}>
                                {t.label}
                                {t.id === 'all' && <span className="seg-count">{orders.length}</span>}
                            </button>
                        ))}
                    </div>
                )}

                {/* List */}
                <div className="orders-list-wrap">
                {loading ? (
                    <div className="order-list">
                        {Array.from({ length: 2 }).map((_, i) => (
                            <div key={i} className="card card-pad">
                                <div className="skeleton" style={{ height: 16, width: '45%', marginBottom: 10 }} />
                                <div className="skeleton" style={{ height: 12, width: '75%', marginBottom: 8 }} />
                                <div className="skeleton" style={{ height: 52, width: '100%' }} />
                            </div>
                        ))}
                    </div>
                ) : !searched ? (
                    <div className="empty">
                        <div className="empty-icon"><i className="fa-solid fa-receipt" /></div>
                        <h3>Tra cứu bằng số điện thoại</h3>
                        <p>
                            Không cần đăng nhập. Nhập <strong>số điện thoại</strong> hoặc <strong>mã đơn</strong> (ví dụ DH464528)
                            bạn đã dùng khi đặt bánh để xem chi tiết và lịch giao.
                        </p>
                        <button type="button" className="btn btn-outline" onClick={() => navigate('/menu')}>Khám phá menu bánh</button>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="empty">
                        <div className="empty-icon"><i className="fa-regular fa-face-frown" /></div>
                        <h3>Không tìm thấy đơn nào</h3>
                        <p>Không có đơn khớp với “{query}”. Kiểm tra lại số điện thoại hoặc mã đơn giúp tiệm nhé.</p>
                        <button type="button" className="btn btn-primary" onClick={() => navigate('/menu')}>Đặt bánh ngay</button>
                    </div>
                ) : (
                    <Reveal stagger className="order-list" key={`${tab}-${filtered.length}`}>
                        {filtered.map((b) => {
                            const badge = statusBadge(b.status, b.paymentStatus);
                            const steps = timelineSteps(b.status, b.paymentStatus);
                            const s = (b.status || '').toLowerCase();
                            const ps = (b.paymentStatus || '').toLowerCase();

                            const isCancelled = s.includes('hủy') || ps === 'cancelled';
                            const isPaid = isFullyPaid(b);
                            const unpaid = !isPaid && !isCancelled;
                            const partial = isPartial(b);
                            const due = amountDueOf(b);
                            const refund = refundDueOf(b);

                            return (
                                <div key={b.id} className="order-card">
                                    <div className="order-top">
                                        <div className="order-code">#{b.orderCode}</div>
                                        <span className={`badge ${badge.cls}`}>{badge.text}</span>
                                    </div>

                                    <div className="order-meta">
                                        {b.customerName} · đặt {formatDate(b.createdAt)}
                                        {b.paymentMethod === 'pay_later' && (
                                            <span style={{ marginLeft: 6, color: '#B45309', fontWeight: 600 }}>
                                                · <i className="fa-regular fa-clock" style={{ marginRight: 2 }} /> Thanh toán sau
                                            </span>
                                        )}
                                    </div>

                                    <div className="order-items">{summarizeItems(b.items)}</div>

                                    {(() => {
                                        let arr = [];
                                        try { arr = typeof b.items === 'string' ? JSON.parse(b.items) : b.items; } catch (e) {}
                                        if (!Array.isArray(arr)) return null;

                                        const boxes = arr.filter(it => it.isBox && Array.isArray(it.items) && it.items.length > 0);
                                        if (boxes.length === 0) return null;

                                        return (
                                            <div className="order-box-details">
                                                {boxes.map((boxIt, bIdx) => (
                                                    <div key={bIdx} className="order-box-item">
                                                        <div className="order-box-title">
                                                            <i className="fa-solid fa-gift" />
                                                            <span>Vị trí các bánh trong {boxIt.name}:</span>
                                                        </div>
                                                        <div className="order-box-slots">
                                                            {boxIt.items.map((subC, sIdx) => (
                                                                <div key={sIdx} className="order-slot-row">
                                                                    <div className="order-slot-main">
                                                                        <span className="order-slot-num">{sIdx + 1}</span>
                                                                        <span className="order-slot-name">{subC.name}</span>
                                                                    </div>
                                                                    {(subC.weight || subC.egg || subC.hasEgg !== undefined) && (
                                                                        <span className="order-slot-tag">
                                                                            {[
                                                                                subC.weight,
                                                                                subC.hasEgg !== undefined ? (subC.hasEgg ? '1 trứng' : 'Không trứng') : subC.egg
                                                                            ].filter(Boolean).join(' · ')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}

                                    <div className="order-deliv">
                                        <i className="fa-solid fa-truck" />
                                        <div>
                                            <div className="order-deliv-date">
                                                {b.delivery_date ? formatDateShort(b.delivery_date) : 'Chưa định ngày'}
                                            </div>
                                            <div className="order-deliv-slot">Khung {b.deliveryTimeSlot || TIME_SLOTS[0]}</div>
                                        </div>
                                    </div>

                                    <div className="timeline">
                                        {steps.map((st, i) => (
                                            <div key={i} className={`tl-step ${st.state}`}>
                                                {i > 0 && <span className={`tl-line ${st.state !== 'todo' ? 'on' : ''}`} />}
                                                <span className="tl-dot"><i className={st.icon} /></span>
                                                <span className="tl-label">{st.label}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="order-foot">
                                        <div>
                                            <div className="t-muted">Tổng đơn</div>
                                            <div className="order-total">{formatCurrency(b.totalAmount)}</div>
                                            {partial && due > 0 && (
                                                <div className="order-due">
                                                    Đã trả {formatCurrency(paidAmountOf(b))} · còn thiếu <strong>{formatCurrency(due)}</strong>
                                                </div>
                                            )}
                                            {refund > 0 && (
                                                <div className="order-refund">Tiệm sẽ hoàn lại {formatCurrency(refund)}</div>
                                            )}
                                        </div>

                                        <div className="order-actions">
                                            <button type="button" className="btn btn-quiet btn-sm" onClick={() => openEdit(b)}>
                                                Đổi ngày
                                            </button>
                                            {unpaid ? (
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm"
                                                    onClick={() => { setPayBill(b); setShowPay(true); }}
                                                >
                                                    <i className="fa-solid fa-qrcode" style={{ fontSize: 12 }} /> {partial ? `Trả thêm ${formatCurrency(due)}` : 'Thanh toán'}
                                                </button>
                                            ) : isPaid ? (
                                                <span className="order-paid-tag">
                                                    <i className="fa-solid fa-circle-check" /> Đã hoàn tất
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </Reveal>
                )}
                </div>
                </div>

                {/* Help rail — desktop */}
                <aside className="orders-help desk-only">
                    <div className="help-card">
                        <div className="help-title">Cần hỗ trợ đặt bánh?</div>
                        <p>Nhắn tiệm nếu bạn quên mã đơn, muốn đổi ngày giao hoặc gộp nhiều hộp vào một đơn.</p>
                        <a href="tel:0856345789" className="btn btn-quiet btn-block btn-sm">
                            <i className="fa-solid fa-phone" /> 0856.345.789
                        </a>
                        <a href="https://zalo.me/0856345789" target="_blank" rel="noreferrer" className="btn btn-quiet btn-block btn-sm">
                            <i className="fa-solid fa-comment-dots" /> Nhắn Zalo
                        </a>
                    </div>

                    <ul className="help-notes">
                        <li><i className="fa-regular fa-clock" /> Đơn chưa thanh toán được giữ chỗ 60 phút.</li>
                        <li><i className="fa-regular fa-calendar-check" /> Đổi ngày giao miễn phí trước 48 giờ.</li>
                        <li><i className="fa-solid fa-qrcode" /> Chuyển khoản VietQR khớp đơn tự động trong vài giây.</li>
                    </ul>
                </aside>
            </div>

            {/* Change delivery date */}
            <Sheet
                open={!!editBill}
                onClose={() => setEditBill(null)}
                subtitle={editBill ? `Đơn #${editBill.orderCode}` : ''}
                title="Đổi ngày giao"
                footer={<button type="button" className="btn btn-primary btn-block" onClick={saveDate}>Lưu ngày giao mới</button>}
            >
                <p className="t-muted" style={{ marginBottom: 14 }}>
                    Tiệm hỗ trợ đổi ngày giao miễn phí trước 48 giờ để xưởng sắp xếp chu đáo.
                </p>

                <div className="dates-2-grid" style={{ marginBottom: 16 }}>
                    {dates.map((d) => (
                        <button
                            key={d.iso}
                            type="button"
                            className={`daychip-wide ${newDate === d.iso ? 'on' : ''} ${d.disabled ? 'off' : ''}`}
                            onClick={() => !d.disabled && setNewDate(d.iso)}
                        >
                            <div className="daychip-dow">{d.dowFull || (d.dow === 'T2' ? 'Thứ Hai' : 'Thứ Sáu')}</div>
                            <div className="daychip-date">{d.day} Tháng 09</div>
                            <div className="daychip-note">{newDate === d.iso ? 'Đang chọn' : d.note}</div>
                        </button>
                    ))}
                </div>

                <div className="sheet-label">Khung giờ nhận</div>
                <div className="slot-grid">
                    {TIME_SLOTS.map((s) => (
                        <button key={s} type="button" className={`slot ${newSlot === s ? 'on' : ''}`} onClick={() => setNewSlot(s)}>
                            {s}
                        </button>
                    ))}
                </div>
            </Sheet>

            {payBill && (
                <React.Suspense fallback={null}>
                    <SepayModal
                        show={showPay}
                        bill={payBill}
                        onClose={() => setShowPay(false)}
                        onPaymentSuccess={(updated) => {
                            // Mirrors the webhook: an order already baking keeps its progress.
                            setOrders((prev) => prev.map((o) => (
                                o.id === updated.id ? {
                                    ...o,
                                    paymentStatus: 'paid',
                                    paidAmount: o.totalAmount,
                                    status: /làm|giao/i.test(o.status || '') ? o.status : 'Đã thanh toán',
                                } : o
                            )));
                            // The sheet closes from its own "Xem trạng thái đơn hàng" button,
                            // so the customer actually sees the confirmation.
                        }}
                    />
                </React.Suspense>
            )}
        </AppShell>
    );
}
